/**
 * The one way the app talks to the API. Same origin under `/api` (Vite proxies it in dev;
 * Caddy routes it in production), cookies included. A refusal is a sentence and who to ask,
 * never a stack trace.
 */
export interface Refusal {
  status: number;
  error: string;
  reason: string;
  ask?: { role: string; name: string };
  /** "Open them" — who the refusal means, where it means somebody (`looks_like`). */
  who?: { id: string; name: string };
  /** Everyone it means, the named one first: "it is not them" is said of all of them. */
  everyone?: { id: string; name: string }[];
}

export class ApiRefused extends Error {
  constructor(readonly refusal: Refusal) {
    super(refusal.reason);
  }
}

export const isRefused = (e: unknown): e is ApiRefused => e instanceof ApiRefused;

export async function api<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    const init: RequestInit = { method, credentials: 'same-origin' };
    if (body !== undefined) {
      init.headers = { 'content-type': 'application/json' };
      init.body = JSON.stringify(body);
    }
    res = await fetch(`/api${path}`, init);
  } catch {
    throw new ApiRefused({
      status: 0,
      error: 'offline',
      reason: 'The server could not be reached.',
    });
  }
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const r = (json ?? {}) as Partial<Refusal>;
    throw new ApiRefused({
      status: res.status,
      error: r.error ?? 'failed',
      reason: r.reason ?? `The server answered ${res.status}.`,
      ...(r.ask ? { ask: r.ask } : {}),
      // What the refusal offers to do about itself. Copying only the sentence and the role left
      // every screen re-deriving these from the words — People.tsx pulled a name out of the
      // sentence with a pattern and searched for it, and found nobody the day it changed.
      ...(r.who ? { who: r.who } : {}),
      ...(r.everyone ? { everyone: r.everyone } : {}),
    });
  }
  return json as T;
}

/** "You do not have this door. Ask a director." — the sentence, with who to ask appended once. */
export function sentence(e: unknown): string {
  if (isRefused(e)) return e.refusal.reason;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
